import {
  DEFAULT_END_WORK_DAY,
  DEFAULT_START_WORK_DAY,
  encodeGlobalId,
} from "@msys/common";
import {
  ellipsisStyle,
  ModalOpenButton,
  normalizeTime,
  useScreenWidth,
} from "@msys/ui";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Fab,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FieldArray, useFormikContext } from "formik";
import moment from "moment";
import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useUnmount } from "react-use";
import { v4 } from "uuid";
import { color } from "../../../common/MuiThemeProvider.js";
import { AutoSave, AutoSaveRef } from "../../commons/form-fields/AutoSave.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { DateTimePickerField } from "../../commons/form-fields/DateTimePickerField.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { durationInMinutesToTime } from "../../utils.js";
import { OrganisationMemberField } from "../organisations/form-fields/OrganisationMemberField.js";
import { ScheduleProjectFragment } from "../schedule/Fragments.generated.js";
import {
  getNextWorkingSessionDate,
  getTotalDurationInMinutes,
} from "./helpers.js";

export interface PlanningSessionsFormRef {
  /**
   * Force trigger saving process from outside
   */
  save: () => void;
}

const FILTERS = { statuses: ["ACTIVATED" as const] };

export const createNewSession = (
  now: moment.Moment,
  projectIsTicket: boolean,
  defaultProjectDuration: number,
  defaultTicketDuration: number,
  lastPlanSession: PlanSessionForm | null,
  startWorkDay: string = DEFAULT_START_WORK_DAY, // should be in HH:mm format
  endWorkDay: string = DEFAULT_END_WORK_DAY // should be in HH:mm format
): PlanSessionForm => {
  const lastFrom = lastPlanSession?.from ?? null;
  const lastTill = lastPlanSession?.till ?? null;
  const [from, till] = getNextWorkingSessionDate(
    now,
    projectIsTicket,
    defaultProjectDuration,
    defaultTicketDuration,
    lastFrom,
    lastTill,
    startWorkDay,
    endWorkDay
  );
  return {
    id: encodeGlobalId("PlanSession", v4()),
    userId: null,
    from,
    till,
    isTentative: false,
  };
};

export interface PlanSessionForm {
  id: string;
  from: moment.Moment;
  till: moment.Moment;
  userId: string | null;
  isTentative: boolean;
}

interface FormValues {
  planSessions: PlanSessionForm[];
}

interface Props {
  project: ScheduleProjectFragment;
  projectIsTicket: boolean;
  projectDuration?: number;
  ticketDuration?: number;
  startWorkDay?: string;
  endWorkDay?: string;
  initialValues: FormValues;
  filterValues: (values: FormValues) => FormValues;
  isEqualForm: (
    v1: FormValues | undefined,
    v2: FormValues | undefined
  ) => boolean;
  onTotalDurationChange?(duration: number): void;
  showTotalDuration?: boolean;
}

export const PlanningSessionsForm = React.forwardRef<
  PlanningSessionsFormRef,
  Props
>(
  (
    {
      project,
      projectIsTicket,
      startWorkDay = DEFAULT_START_WORK_DAY,
      endWorkDay = DEFAULT_END_WORK_DAY,
      projectDuration = 0,
      ticketDuration = 0,
      initialValues,
      filterValues,
      isEqualForm,
      onTotalDurationChange,
      showTotalDuration = true,
    },
    forwardedRef
  ) => {
    const {
      values: { planSessions },
    } = useFormikContext<FormValues>();
    const { isMinTablet, isMinDesktop } = useScreenWidth();
    const { t } = useTranslate(["PlanningModal", "Global"]);

    const autoSaveTimeoutId = useRef<ReturnType<typeof setTimeout>>();
    const autoSaveRef = useRef<AutoSaveRef>(null);

    const forceSave = useCallback(() => {
      if (autoSaveTimeoutId.current) clearTimeout(autoSaveTimeoutId.current);
      autoSaveTimeoutId.current = setTimeout(() => {
        autoSaveRef.current?.save();
      }, 250);
    }, []);

    useUnmount(() => {
      if (autoSaveTimeoutId.current) clearTimeout(autoSaveTimeoutId.current);
    });

    useImperativeHandle(forwardedRef, () => ({
      save: forceSave,
    }));

    const containerRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = useCallback(() => {
      if (containerRef.current)
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }, []);

    const now = useMemo(() => moment().add(1, "hours").startOf("hour"), []);

    const durationsCache = useRef(new Map());

    const durations: { [id: string]: { isActive: boolean; minutes: number } } =
      useMemo(
        () =>
          planSessions.reduce((acc, session) => {
            const key =
              session.from && session.till
                ? `${session.from.format()}-${session.till.format()}`
                : null;

            let duration = key
              ? (durationsCache.current.get(key) ?? null)
              : null;
            const isActive = Boolean(session.userId);

            if (duration === null && key !== null) {
              // expensive operation
              duration = getTotalDurationInMinutes(
                session.from,
                session.till,
                normalizeTime(startWorkDay),
                normalizeTime(endWorkDay)
              );
              durationsCache.current.set(key, duration);
            }

            return { ...acc, [session.id]: { isActive, minutes: duration } };
          }, {}),
        [planSessions, startWorkDay, endWorkDay]
      );

    const totalDuration = useMemo(() => {
      return Object.keys(durations).reduce(
        (acc, id) => acc + (durations[id].isActive ? durations[id].minutes : 0),
        0
      );
    }, [durations]);

    React.useEffect(() => {
      onTotalDurationChange?.(totalDuration);
    }, [totalDuration, onTotalDurationChange]);

    return (
      <FieldArray
        name="planSessions"
        render={({ remove, push }) => (
          <Box
            sx={{
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isMinTablet && (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Grid
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {t("Team member", {
                        ns: "PlanningModal",
                      })}
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 3,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {t("From", {
                        ns: "PlanningModal",
                      })}
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 3,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {t("Until", {
                        ns: "PlanningModal",
                      })}
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        align="left"
                        component="div"
                      >
                        {t("Tentative", {
                          ns: "PlanningModal",
                        })}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        align="right"
                        component="div"
                      >
                        {t("Duration", {
                          ns: "PlanningModal",
                        })}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
                <IconButton
                  color="secondary"
                  size="small"
                  onClick={() => {
                    push(
                      createNewSession(
                        now,
                        projectIsTicket,
                        projectDuration,
                        ticketDuration,
                        planSessions?.[planSessions.length - 1] ?? null,
                        startWorkDay,
                        endWorkDay
                      )
                    );
                    setTimeout(scrollToBottom);
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Stack>
            )}
            <Stack
              direction="column"
              spacing={0.5}
              ref={containerRef}
              sx={{
                alignItems: "stretch",
                overflow: "auto",
                flexGrow: 1,
                flexShrink: 1,
                paddingBottom: !isMinDesktop ? "38px" : undefined,
              }}
            >
              {planSessions.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => {
                      push(
                        createNewSession(
                          now,
                          projectIsTicket,
                          projectDuration,
                          ticketDuration,
                          planSessions?.[planSessions.length - 1] ?? null,
                          startWorkDay,
                          endWorkDay
                        )
                      );
                      setTimeout(scrollToBottom);
                    }}
                    startIcon={<AddIcon />}
                  >
                    {t("New session", {
                      ns: "PlanningModal",
                    })}
                  </Button>
                </Box>
              ) : (
                planSessions.map((session, index) => {
                  const duration = durationInMinutesToTime(
                    durations[session.id].minutes
                  );
                  const isActive = Boolean(session.userId);
                  return (
                    <React.Fragment key={session.id}>
                      {!isMinTablet && index > 0 && <Divider />}
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Grid
                          container
                          spacing={1}
                          sx={{
                            alignItems: "flex-start",
                            width: "100%",
                          }}
                        >
                          <Grid
                            size={{
                              xs: 12,
                              sm: 4,
                              md: 4,
                            }}
                          >
                            <OrganisationMemberField
                              inputLabel=""
                              // placeholder={t("Team member", {
                              //   ns: "PlanningModal",
                              // })}
                              name={`planSessions[${index}].userId`}
                              size="extra-small"
                              onChanged={() => {
                                forceSave();
                              }}
                              disabled={false}
                              filters={FILTERS}
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              sm: 4,
                              md: 3,
                            }}
                          >
                            <DateTimePickerField
                              label=""
                              placeholder={t("From", {
                                ns: "PlanningModal",
                              })}
                              name={`planSessions[${index}].from`}
                              disabled={false}
                              textFieldProps={{ size: "extra-small" }}
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              sm: 4,
                              md: 3,
                            }}
                          >
                            <DateTimePickerField
                              label=""
                              placeholder={t("Until", {
                                ns: "PlanningModal",
                              })}
                              name={`planSessions[${index}].till`}
                              disabled={false}
                              textFieldProps={{ size: "extra-small" }}
                              // minValue={moment(session.from).add(1, "minute")}
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 2,
                            }}
                          >
                            {!isMinTablet ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <CheckboxField
                                  name={`planSessions[${index}].isTentative`}
                                  label={t("Tentative", {
                                    ns: "PlanningModal",
                                  })}
                                  disabled={false}
                                  size="small"
                                  sx={{ padding: "5px", ml: "4px" }}
                                />
                                <Typography
                                  align="right"
                                  style={{
                                    color: !isActive ? color.grey : undefined,
                                    ...ellipsisStyle,
                                  }}
                                >
                                  {t("Duration", {
                                    ns: "PlanningModal",
                                  })}
                                  : {duration} h
                                </Typography>
                              </Stack>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "30px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <CheckboxField
                                    name={`planSessions[${index}].isTentative`}
                                    label={""}
                                    disabled={false}
                                    size="small"
                                    sx={{ padding: "5px", ml: "4px" }}
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    height: "30px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Typography
                                    align="right"
                                    style={{
                                      color: !isActive ? color.grey : undefined,
                                      ...ellipsisStyle,
                                    }}
                                  >
                                    {duration} h
                                  </Typography>
                                </Box>
                              </Stack>
                            )}
                          </Grid>
                        </Grid>
                        <Box
                          sx={{
                            height: "30px",
                            display: "flex",
                            alignItems: "center",
                            alignSelf: !isMinTablet ? "center" : undefined,
                          }}
                        >
                          {!isActive ? (
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => remove(index)}
                              sx={{ width: "30px", height: "30px" }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          ) : (
                            <ModalOpenButton
                              Modal={ConfirmModal}
                              modalProps={{
                                text: t("Session will be permanently removed", {
                                  ns: "PlanningModal",
                                }),
                                handleConfirm: async () => {
                                  await remove(index);
                                  await forceSave();
                                },
                              }}
                            >
                              <IconButton
                                color="secondary"
                                size="small"
                                sx={{ width: "30px", height: "30px" }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ModalOpenButton>
                          )}
                        </Box>
                      </Stack>
                    </React.Fragment>
                  );
                })
              )}
            </Stack>
            {showTotalDuration ? (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  borderTop: `1px solid ${color.primary}`,
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{
                    width: "100%",
                  }}
                >
                  <Grid
                    size={{
                      xs: 4,
                      md: 4,
                    }}
                  >
                    <Box
                      style={ellipsisStyle}
                      sx={{
                        pt: 0.5,
                      }}
                    >
                      <AutoSave
                        ref={autoSaveRef}
                        isEqualForm={isEqualForm}
                        enableReinitialize
                        initialValues={initialValues}
                        filterValues={filterValues}
                        variant="body1"
                        justifyContent="flex-start"
                      />
                    </Box>
                  </Grid>
                  <Grid
                    size={{
                      xs: 8,
                      md: 8,
                    }}
                  >
                    <Box
                      sx={{
                        pt: 0.5,
                      }}
                    >
                      <Typography align="right">
                        {t("Total duration", { ns: "PlanningModal" })}:{" "}
                        {durationInMinutesToTime(totalDuration)} h
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <div style={{ width: 30 }} />
              </Stack>
            ) : (
              <AutoSave
                ref={autoSaveRef}
                isEqualForm={isEqualForm}
                enableReinitialize
                initialValues={initialValues}
                filterValues={filterValues}
                variant="body1"
                justifyContent="flex-end"
              />
            )}

            {!isMinDesktop && (
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  flexShrink: 0,
                  flexGrow: 0,
                  height: 0,
                }}
              >
                <Fab
                  variant="extended"
                  size="small"
                  color="secondary"
                  onClick={() => {
                    push(
                      createNewSession(
                        now,
                        projectIsTicket,
                        projectDuration,
                        ticketDuration,
                        planSessions?.[planSessions.length - 1] ?? null,
                        startWorkDay,
                        endWorkDay
                      )
                    );
                    setTimeout(scrollToBottom);
                  }}
                  sx={{ position: "absolute", bottom: 0 }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  {t("New session", { ns: "PlanningModal" })}
                </Fab>
              </Box>
            )}
          </Box>
        )}
      />
    );
  }
);
